const ROTATOR_SPEED_FACTOR = 1;
const MAIN_IMAGE_ASPECT_RATIO = 4 / 3;

const withSpeed = (ms) => Math.round(ms * ROTATOR_SPEED_FACTOR);

function syncImageAttributes(target, source) {
	if (!target || !source) return;
	const attrs = ['src', 'srcset', 'sizes', 'alt', 'loading', 'decoding', 'width', 'height'];
	attrs.forEach((attr) => {
		const value = source.getAttribute(attr);
		if (value !== null) {
			target.setAttribute(attr, value);
		} else {
			target.removeAttribute(attr);
		}
	});
}

function applyImageAttributes(target, attrs = {}) {
	if (!target) return;
	const attrMap = {
		src: attrs.src || '',
		srcset: attrs.srcset || '',
		sizes: attrs.sizes || '',
		alt: attrs.alt || '',
		loading: attrs.loading || '',
		decoding: attrs.decoding || '',
		width: attrs.width || '',
		height: attrs.height || '',
	};
	Object.entries(attrMap).forEach(([key, value]) => {
		if (value) {
			target.setAttribute(key, value);
		} else {
			target.removeAttribute(key);
		}
	});
}

function setNewIconBadge(container, enabled, modifier = '') {
	if (!container) return;
	container.querySelectorAll(':scope > .image-rotator__new-icon').forEach((badge) => badge.remove());
	if (!enabled) return;

	const badge = document.createElement('span');
	badge.className = `image-rotator__new-icon${modifier ? ` ${modifier}` : ''}`;
	badge.setAttribute('aria-hidden', 'true');
	badge.textContent = 'NEU';
	container.appendChild(badge);
}

function limitSrcsetByMaxWidth(srcset, maxWidth = 900) {
	if (!srcset || typeof srcset !== 'string') return '';
	const candidates = srcset
		.split(',')
		.map((part) => part.trim())
		.filter(Boolean)
		.filter((candidate) => {
			const match = candidate.match(/\s+(\d+)w$/);
			if (!match) return true;
			const width = Number.parseInt(match[1], 10);
			return Number.isFinite(width) ? width <= maxWidth : true;
		});
	return candidates.join(', ');
}

function getWrappedIndex(index, length) {
	if (!length) return 0;
	return (index + length) % length;
}

function ensureMainLayer(slot, currentImage) {
	if (!slot) return null;

	let center = slot.querySelector('.image-rotator__main-center');
	if (!center) {
		center = slot.querySelector('.image-rotator__image');
		if (!center) return null;
		center.classList.add('image-rotator__main-center');
	}

	let left = slot.querySelector('.image-rotator__main-left');
	if (!left) {
		left = center.cloneNode(true);
		left.className = 'image-rotator__image image-rotator__main-left';
	}

	let right = slot.querySelector('.image-rotator__main-right');
	if (!right) {
		right = center.cloneNode(true);
		right.className = 'image-rotator__image image-rotator__main-right';
	}

	// Reihenfolge fix halten: left, center, right.
	if (left.parentNode !== slot || left.nextSibling !== center) {
		slot.insertBefore(left, center);
	}
	if (right.parentNode !== slot || right.previousSibling !== center) {
		slot.appendChild(right);
	}

	if (currentImage) {
		syncImageAttributes(center, currentImage);
	}

	return { left, center, right };
}

function setMainLayerImages(layer, leftImage, centerImage, rightImage) {
	if (!layer) return;
	if (leftImage) syncImageAttributes(layer.left, leftImage);
	if (centerImage) syncImageAttributes(layer.center, centerImage);
	if (rightImage) syncImageAttributes(layer.right, rightImage);
}

function rotateMainLayerRoles(slot, direction) {
	const layer = ensureMainLayer(slot);
	if (!layer) return null;

	if (direction > 0) {
		layer.left.className = 'image-rotator__image image-rotator__main-right';
		layer.center.className = 'image-rotator__image image-rotator__main-left';
		layer.right.className = 'image-rotator__image image-rotator__main-center';
	} else {
		layer.left.className = 'image-rotator__image image-rotator__main-center';
		layer.center.className = 'image-rotator__image image-rotator__main-right';
		layer.right.className = 'image-rotator__image image-rotator__main-left';
	}

	return ensureMainLayer(slot);
}

function resetMainLayerPositions(slot, gapPx = 0) {
	const layer = ensureMainLayer(slot);
	if (!layer) return;

	const offset = Math.max(0, Number(gapPx) || 0);
	const delta = `calc(100% + ${offset}px)`;

	layer.left.style.transition = 'none';
	layer.center.style.transition = 'none';
	layer.right.style.transition = 'none';
	layer.left.style.transform = `translate3d(calc(-1 * ${delta}), 0, 0)`;
	layer.center.style.transform = 'translate3d(0, 0, 0)';
	layer.right.style.transform = `translate3d(${delta}, 0, 0)`;
}

function applyMainTransition(slot, layerImages, direction = 1, duration = withSpeed(520), gapPx = 0) {
	if (!slot || !layerImages?.from || !layerImages?.to) return;

	const layer = ensureMainLayer(slot, layerImages.from);
	if (!layer) return;

	const offset = Math.max(0, Number(gapPx) || 0);
	const delta = `calc(100% + ${offset}px)`;
	const easing = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

	setMainLayerImages(layer, layerImages.leftStart || layerImages.from, layerImages.from, layerImages.rightStart || layerImages.to);
	resetMainLayerPositions(slot, gapPx);

	requestAnimationFrame(() => {
		layer.left.style.transition = `transform ${duration}ms ${easing}`;
		layer.center.style.transition = `transform ${duration}ms ${easing}`;
		layer.right.style.transition = `transform ${duration}ms ${easing}`;

		if (direction > 0) {
			layer.left.style.transform = `translate3d(calc(-2 * ${delta}), 0, 0)`;
			layer.center.style.transform = `translate3d(calc(-1 * ${delta}), 0, 0)`;
			layer.right.style.transform = 'translate3d(0, 0, 0)';
		} else {
			layer.left.style.transform = 'translate3d(0, 0, 0)';
			layer.center.style.transform = `translate3d(${delta}, 0, 0)`;
			layer.right.style.transform = `translate3d(calc(2 * ${delta}), 0, 0)`;
		}
	});

	window.setTimeout(() => {
		const rotated = rotateMainLayerRoles(slot, direction);
		if (rotated) {
			if (direction > 0) {
				syncImageAttributes(rotated.right, layerImages.recycle || layerImages.to);
			} else {
				syncImageAttributes(rotated.left, layerImages.recycle || layerImages.to);
			}
		}
		resetMainLayerPositions(slot, gapPx);
	}, duration + 40);
}

function initSingleImageRotator(root) {
	const sources = Array.from(root.querySelectorAll('.image-rotator__source'));
	if (!sources.length) return;

	const slides = sources
		.map((source) => {
			const image = source.querySelector('img');
			if (!image) return null;
			const thumb = {
				src: source.dataset.thumbSrc || '',
				srcset: source.dataset.thumbSrcset || '',
				sizes: source.dataset.thumbSizes || '',
			};
			return {
				heading: source.querySelector('.image-rotator__source-heading')?.innerHTML || source.dataset.heading || '',
				description: source.querySelector('.image-rotator__source-description')?.innerHTML || source.dataset.description || '',
				newIcon: source.dataset.newIcon === 'true',
				image,
				thumb,
			};
		})
		.filter(Boolean);
	if (!slides.length) return;

	const prevButton = root.querySelector('.image-rotator__button--prev');
	const nextButton = root.querySelector('.image-rotator__button--next');
	const layout = root.querySelector('.image-rotator__layout');
	const largeSlot = root.querySelector('.image-rotator__slot--large');
	const thumbViewport = root.querySelector('.image-rotator__thumb-viewport');
	const thumbTrack = root.querySelector('.image-rotator__thumb-track');
	const textWrap = root.querySelector('.image-rotator__text');
	const heading = root.querySelector('.image-rotator__heading');
	let description = root.querySelector('.image-rotator__description');
	const headingBasedDescriptionClass = heading?.className
		? heading.className
			.replace(/\bimage-rotator__heading\b/g, 'image-rotator__description')
			.replace(/\bfont-bold\b/g, '')
			.replace(/\s+/g, ' ')
			.trim()
		: '';
	const descriptionClassName = description?.className || headingBasedDescriptionClass || 'image-rotator__description';
	const pagination = root.querySelector('.image-rotator__pagination');
	const progressFill = root.querySelector('.image-rotator__progress-fill');
	if (!layout || !largeSlot || !thumbTrack || !thumbViewport) return;
	if (pagination && layout.classList.contains('max-w-narrow')) {
		pagination.classList.add('max-w-narrow');
	}

	let current = Number(root.dataset.currentIndex || 0);
	if (!Number.isFinite(current) || current < 0) current = 0;
	current %= slides.length;

	let thumbBase = 1 + ((current + 1) % slides.length);
	let isAnimating = false;
	let hasUserInteracted = false;
	let stepPx = 0;
	let trackGap = 0;
	let layoutGapX = 0;

	const syncCurrentSlideState = () => {
		const total = slides.length;
		if (!total) return;

		const currentSlide = slides[current] || null;
		const previousSlide = slides[getWrappedIndex(current - 1, total)] || null;
		const nextSlide = slides[getWrappedIndex(current + 1, total)] || null;
		const layer = ensureMainLayer(largeSlot, currentSlide?.image);

		if (layer) {
			setMainLayerImages(layer, previousSlide?.image, currentSlide?.image, nextSlide?.image);
			resetMainLayerPositions(largeSlot, layoutGapX);
		}

		updateTextAndProgress();
		root.dataset.currentIndex = String(current);
	};

	const isElementVisible = (element) => {
		if (!element) return false;
		const style = window.getComputedStyle(element);
		if (style.display === 'none' || style.visibility === 'hidden') return false;
		return element.getClientRects().length > 0;
	};

	const syncLayoutHeightFromMainImage = () => {
		layout.style.height = '';
		if (window.innerWidth < 768) {
			thumbViewport.style.height = '';
			return;
		}

		const largeWidth = largeSlot.getBoundingClientRect().width || 0;
		if (!largeWidth) {
			thumbViewport.style.height = '';
			return;
		}

		const mainHeight = largeWidth / MAIN_IMAGE_ASPECT_RATIO;
		thumbViewport.style.height = `${Math.round(mainHeight)}px`;
	};

	const measureTrack = () => {
		syncLayoutHeightFromMainImage();
		const layoutComputed = window.getComputedStyle(layout);
		layoutGapX = Number.parseFloat(layoutComputed.columnGap || layoutComputed.gap || '0') || 0;
		const computed = window.getComputedStyle(thumbTrack);
		trackGap = Number.parseFloat(computed.rowGap || computed.gap || '0') || 0;
		const viewportRect = thumbViewport.getBoundingClientRect();
		if (!viewportRect.height) return;

		const itemHeight = Math.max(1, (viewportRect.height - trackGap) / 2);
		thumbTrack.querySelectorAll('.image-rotator__thumb-item').forEach((item) => {
			item.style.height = `${itemHeight}px`;
			item.style.minHeight = `${itemHeight}px`;
		});
		stepPx = itemHeight + trackGap;
	};

	const setTrackTransform = (base, animate = false, duration = withSpeed(760)) => {
		if (!stepPx) measureTrack();
		if (!stepPx) return;
		thumbTrack.style.transition = animate
			? `transform ${duration}ms cubic-bezier(0.22, 0.61, 0.36, 1)`
			: 'none';
		thumbTrack.style.transform = `translateY(-${base * stepPx}px)`;
	};

	const refreshLayout = () => {
		if (!isElementVisible(root)) return;
		syncLayoutHeightFromMainImage();
		measureTrack();
		setTrackTransform(thumbBase, false);
		resetMainLayerPositions(largeSlot, layoutGapX);
	};

	const refreshLayoutWithRetry = (attempt = 0) => {
		if (!isElementVisible(root)) return;
		refreshLayout();
		const hasViewportHeight = (thumbViewport.getBoundingClientRect().height || 0) > 0;
		const hasLargeWidth = (largeSlot.getBoundingClientRect().width || 0) > 0;
		if (hasViewportHeight && hasLargeWidth) return;
		if (attempt >= 8) return;
		window.setTimeout(() => refreshLayoutWithRetry(attempt + 1), 60);
	};

	const rebuildTrack = () => {
		thumbTrack.innerHTML = '';
		const total = slides.length;
		if (!total) return;

		const sequence = [(total - 1), ...Array.from({ length: total }, (_, i) => i), 0, 1 % total];
		sequence.forEach((index) => {
			const item = document.createElement('div');
			item.className = 'image-rotator__thumb-item';
			item.dataset.sourceIndex = String(index);
			const image = slides[index].image.cloneNode(true);
			const thumbSrcset = limitSrcsetByMaxWidth(slides[index]?.thumb?.srcset || '', 900);
			applyImageAttributes(image, {
				src: slides[index]?.thumb?.src || image.getAttribute('src') || '',
				srcset: thumbSrcset,
				sizes: '(max-width: 767px) 100vw, 380px',
				alt: image.getAttribute('alt') || '',
				loading: image.getAttribute('loading') || 'lazy',
				decoding: image.getAttribute('decoding') || 'async',
				width: '',
				height: '',
			});
			image.classList.add('image-rotator__image');
			item.appendChild(image);
			setNewIconBadge(item, !!slides[index]?.newIcon, 'image-rotator__new-icon--thumb');
			thumbTrack.appendChild(item);
		});

		syncLayoutHeightFromMainImage();
		measureTrack();
		thumbBase = 1 + ((current + 1) % total);
		setTrackTransform(thumbBase, false);
	};

	const updateTextAndProgress = () => {
		const slide = slides[current] || null;
		const headingText = slide?.heading || '';
		const descriptionText = slide?.description || '';
		setNewIconBadge(largeSlot, !!slide?.newIcon);
		const hasText = Boolean(headingText.trim() || descriptionText.trim());
		if (heading) heading.innerHTML = headingText;
		if (descriptionText.trim()) {
			if (!description && textWrap) {
				description = document.createElement('p');
				description.className = descriptionClassName;
				textWrap.appendChild(description);
			}
			if (description) description.innerHTML = descriptionText;
		} else if (description) {
			description.remove();
			description = null;
		}
		if (textWrap) {
			textWrap.hidden = !hasText;
		}
		if (progressFill) {
			const progress = slides.length > 0 ? (current + 1) / slides.length : 0;
			progressFill.style.transform = `scaleX(${progress})`;
		}
	};

	const updateMainImage = (step, fromIndex, toIndex) => {
		const total = slides.length;
		const fromImage = slides[fromIndex]?.image;
		const toImage = slides[toIndex]?.image;
		if (!fromImage || !toImage) return;

		const prevFromImage = slides[getWrappedIndex(fromIndex - 1, total)]?.image || fromImage;
		const nextFromImage = slides[getWrappedIndex(fromIndex + 1, total)]?.image || fromImage;
		const prevToImage = slides[getWrappedIndex(toIndex - 1, total)]?.image || toImage;
		const nextToImage = slides[getWrappedIndex(toIndex + 1, total)]?.image || toImage;

		const layerImages = step > 0
			? {
				leftStart: prevFromImage,
				from: fromImage,
				rightStart: toImage,
				to: toImage,
				recycle: nextToImage,
			}
			: {
				leftStart: toImage,
				from: fromImage,
				rightStart: nextFromImage,
				to: toImage,
				recycle: prevToImage,
			};

		applyMainTransition(largeSlot, layerImages, step, withSpeed(520), layoutGapX);
	};

	const go = (step) => {
		if (isAnimating || slides.length <= 1) return;
		hasUserInteracted = true;
		isAnimating = true;
		syncLayoutHeightFromMainImage();
		measureTrack();

		const total = slides.length;
		const fromIndex = current;
		current = (current + step + total) % total;

		updateMainImage(step, fromIndex, current);
		updateTextAndProgress();

		let targetBase = 1 + ((current + 1) % total);
		if (step > 0 && thumbBase === total && targetBase === 1) {
			targetBase = total + 1;
		}
		if (step < 0 && thumbBase === 1 && targetBase === total) {
			targetBase = 0;
		}

		const duration = withSpeed(760);
		setTrackTransform(targetBase, true, duration);

		window.setTimeout(() => {
			thumbBase = 1 + ((current + 1) % total);
			setTrackTransform(thumbBase, false);
			syncCurrentSlideState();
			isAnimating = false;
		}, duration + 30);
	};

	// Touch-Swipe (vor allem für Mobile)
	let touchStartX = 0;
	let touchStartY = 0;
	let touchActive = false;
	let touchConsumed = false;
	const SWIPE_THRESHOLD = 24;
	const VERTICAL_CANCEL_THRESHOLD = 24;

	const onTouchStart = (event) => {
		if (!event.touches || event.touches.length !== 1) return;
		const t = event.touches[0];
		touchStartX = t.clientX;
		touchStartY = t.clientY;
		touchActive = true;
		touchConsumed = false;
	};

	const onTouchMove = (event) => {
		if (!touchActive || touchConsumed || !event.touches || event.touches.length !== 1) return;
		const t = event.touches[0];
		const dx = t.clientX - touchStartX;
		const dy = t.clientY - touchStartY;

		// Vertikales Scrollen priorisieren.
		if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > VERTICAL_CANCEL_THRESHOLD) {
			touchActive = false;
		}
	};

	const onTouchEnd = (event) => {
		if (!touchActive || touchConsumed) {
			touchActive = false;
			return;
		}

		const t = event.changedTouches && event.changedTouches[0];
		if (!t) {
			touchActive = false;
			return;
		}

		const dx = t.clientX - touchStartX;
		const dy = t.clientY - touchStartY;
		touchActive = false;

		// Nur klare horizontale Gesten auslösen.
		if (Math.abs(dx) < SWIPE_THRESHOLD) return;
		if (Math.abs(dx) <= Math.abs(dy)) return;

		touchConsumed = true;
		if (dx < 0) {
			go(1); // swipe left -> next
		} else {
			go(-1); // swipe right -> prev
		}
	};

	prevButton?.addEventListener('click', (event) => {
		event.preventDefault();
		go(-1);
	});
	nextButton?.addEventListener('click', (event) => {
		event.preventDefault();
		go(1);
	});

	largeSlot.addEventListener('touchstart', onTouchStart, { passive: true });
	largeSlot.addEventListener('touchmove', onTouchMove, { passive: true });
	largeSlot.addEventListener('touchend', onTouchEnd, { passive: true });
	largeSlot.addEventListener('touchcancel', () => {
		touchActive = false;
		touchConsumed = false;
	}, { passive: true });

	// Pointer-Events Fallback (robuster auf manchen Mobile/WebView-Setups)
	let pointerStartX = 0;
	let pointerStartY = 0;
	let pointerActive = false;

	largeSlot.addEventListener('pointerdown', (event) => {
		// Nur primäre Touch/Stift-Zeiger behandeln.
		if (event.pointerType === 'mouse') return;
		pointerStartX = event.clientX;
		pointerStartY = event.clientY;
		pointerActive = true;
	});

	largeSlot.addEventListener('pointerup', (event) => {
		if (!pointerActive) return;
		pointerActive = false;
		if (event.pointerType === 'mouse') return;
		const dx = event.clientX - pointerStartX;
		const dy = event.clientY - pointerStartY;
		if (Math.abs(dx) < SWIPE_THRESHOLD) return;
		if (Math.abs(dx) <= Math.abs(dy)) return;
		if (dx < 0) go(1);
		else go(-1);
	});

	largeSlot.addEventListener('pointercancel', () => {
		pointerActive = false;
	});

	window.addEventListener('resize', () => {
		refreshLayoutWithRetry();
	});

	rebuildTrack();
	syncCurrentSlideState();

	const firstMainImage = largeSlot.querySelector('img');
	if (firstMainImage && !firstMainImage.complete) {
		firstMainImage.addEventListener('load', () => {
			if (hasUserInteracted || isAnimating) return;
			refreshLayoutWithRetry();
		}, { once: true });
	}

	if (typeof window.ResizeObserver !== 'undefined') {
		const resizeObserver = new window.ResizeObserver(() => {
			if (isAnimating) return;
			refreshLayoutWithRetry();
		});
		resizeObserver.observe(largeSlot);
	}

	// Rotator liegt oft in Tabs. Wenn ein verstecktes Panel sichtbar wird,
	// müssen die Maße neu berechnet werden.
	const panel = root.closest('.tabs__panel');
	if (panel) {
		const panelObserver = new MutationObserver(() => {
			if (!isElementVisible(panel)) return;
			window.requestAnimationFrame(() => {
				refreshLayout();
			});
		});
		panelObserver.observe(panel, {
			attributes: true,
			attributeFilter: ['class', 'hidden', 'style'],
		});
	}

	// Falls der Rotator direkt in sichtbaren Inhalt geladen wird:
	refreshLayoutWithRetry();
	window.setTimeout(() => {
		if (hasUserInteracted || isAnimating) return;
		syncCurrentSlideState();
	}, 80);
}

export function initImageRotator(context = document) {
	context.querySelectorAll('.image-rotator').forEach((root) => {
		if (root.dataset.imageRotatorInit === '1') return;
		root.dataset.imageRotatorInit = '1';
		initSingleImageRotator(root);
	});
}

window.CarthagoImageRotator = {
	initAll: initImageRotator,
};

document.addEventListener('carthago:init', () => initImageRotator(document));

export default initImageRotator;
